import { useRef } from 'react';

const THEMES = {
  paper: {
    id: 'paper',
    dot: '#1A3828',
    bg: '#F3EEE3',
    bgGrad: 'radial-gradient(120% 80% at 30% 0%, #FBF7ED 0%, #F3EEE3 60%, #EAE3D2 100%)',
    surface: 'rgba(27,23,15,0.035)',
    surfaceStrong: 'rgba(27,23,15,0.06)',
    border: 'rgba(27,23,15,0.08)',
    borderStrong: 'rgba(27,23,15,0.15)',
    text: '#1C1812',
    textMuted: 'rgba(28,24,18,0.6)',
    textDim: 'rgba(28,24,18,0.35)',
    accent: '#1A3828',
    accentText: '#F3EEE3',
    accentSoft: 'rgba(26,56,40,0.1)',
    chipBg: 'rgba(26,56,40,0.09)',
    chipText: '#1A3828',
    hotBg: 'rgba(26,56,40,0.12)',
    hotText: '#1A3828',
    modalBg: '#FBF7ED',
    mono: "'Montserrat', sans-serif",
  },
  forest: {
    id: 'forest',
    dot: '#F5C842',
    bg: '#0D2218',
    bgGrad: 'radial-gradient(130% 80% at 20% 0%, #122B1E 0%, #0D2218 60%, #091A12 100%)',
    surface: 'rgba(255,255,255,0.045)',
    surfaceStrong: 'rgba(255,255,255,0.08)',
    border: 'rgba(255,255,255,0.09)',
    borderStrong: 'rgba(255,255,255,0.18)',
    text: '#FFFFFF',
    textMuted: 'rgba(255,255,255,0.58)',
    textDim: 'rgba(255,255,255,0.3)',
    accent: '#F5C842',
    accentText: '#0D2218',
    accentSoft: 'rgba(245,200,66,0.15)',
    chipBg: 'rgba(245,200,66,0.12)',
    chipText: '#F5C842',
    hotBg: 'rgba(245,200,66,0.15)',
    hotText: '#F5C842',
    modalBg: '#0D2218',
    mono: "'Montserrat', sans-serif",
  },
  mono: {
    id: 'mono',
    dot: '#FFFFFF',
    bg: '#0A0A0A',
    bgGrad: '#0A0A0A',
    surface: 'rgba(255,255,255,0.04)',
    surfaceStrong: 'rgba(255,255,255,0.08)',
    border: 'rgba(255,255,255,0.1)',
    borderStrong: 'rgba(255,255,255,0.22)',
    text: '#FFFFFF',
    textMuted: 'rgba(255,255,255,0.55)',
    textDim: 'rgba(255,255,255,0.28)',
    accent: '#FFFFFF',
    accentText: '#0A0A0A',
    accentSoft: 'rgba(255,255,255,0.1)',
    chipBg: 'rgba(255,255,255,0.08)',
    chipText: 'rgba(255,255,255,0.85)',
    hotBg: 'rgba(255,255,255,0.1)',
    hotText: 'rgba(255,255,255,0.85)',
    modalBg: '#111111',
    mono: "'Montserrat', sans-serif",
  },
};

// Aktives Theme (für Island 2 / nicht-Nav-Teile — bleibt Paper)
const T = THEMES.paper;

const MONTH_NAMES = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
const DAY_NAMES   = ['Sonntag','Montag','Dienstag','Mittwoch','Donnerstag','Freitag','Samstag'];



// ─── Constants & Helpers ─────────────────────────────────────
const PROFIL_FELDER = ['vorname','nachname','email','telefon','strasse','hausnummer','plz','ort'];
function berechneBasisPct(profile) {
  if (!profile) return 0;
  return Math.round(PROFIL_FELDER.filter(k => profile[k] && String(profile[k]).trim().length > 0).length * 12.5);
}
function naechsterProfilSchritt(profile) {
  if (!profile?.vorname?.trim())    return 'Trage deinen Vornamen ein';
  if (!profile?.nachname?.trim())   return 'Ergänze deinen Nachnamen';
  if (!profile?.email?.trim())      return 'Füge deine E-Mail-Adresse hinzu';
  if (!profile?.telefon?.trim())    return 'Ergänze deine Telefonnummer';
  if (!profile?.strasse?.trim())    return 'Gib deine Straße an';
  if (!profile?.hausnummer?.trim()) return 'Ergänze deine Hausnummer';
  if (!profile?.plz?.trim())        return 'Trage deine Postleitzahl ein';
  if (!profile?.ort?.trim())        return 'Gib deinen Wohnort an';
  return null;
}
function parseMatchScore(analyse) {
  if (!analyse || typeof analyse !== 'string') return null;
  const m = analyse.match(/Übereinstimmung[:\s]+\[?\s*(\d+)\s*\/\s*10\s*\]?/i);
  if (!m) return null;
  return Math.round(parseInt(m[1], 10) * 10);
}
function extractPreviewTitle(text) {
  if (!text) return 'Stellenanzeige';
  const line = text.split('\n').map(l => l.trim()).find(l => l.length > 3);
  return line ? line.slice(0, 60) : 'Stellenanzeige';
}
const PLATZHALTER = ['Neue Stellenanzeige', '<UNKNOWN>', 'UNKNOWN'];

function useLongPress(onLongPress, delay = 600) {
  const timer = useRef(null);
  const fired = useRef(false);

  const start = (e) => {
    fired.current = false;
    timer.current = setTimeout(() => {
      fired.current = true;
      if (navigator.vibrate) navigator.vibrate(40);
      onLongPress(e);
    }, delay);
  };

  const cancel = () => clearTimeout(timer.current);

  return {
    onTouchStart: start,
    onTouchEnd: cancel,
    onTouchMove: cancel,
    onMouseDown: start,
    onMouseUp: cancel,
    onMouseLeave: cancel,
  };
}

const CAL_MONTHS = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
const CAL_DOW    = ['Mo','Di','Mi','Do','Fr','Sa','So'];

function buildCalGrid(year, month) {
  const first = new Date(year, month, 1);
  const last  = new Date(year, month + 1, 0);
  const startDow = (first.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= last.getDate(); d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function calTodayStr() { return new Date().toISOString().slice(0, 10); }

function formatMobileDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTagLabel(ds) {
  return new Date(ds + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });
}

function highlightParts(text, query) {
  if (!query.trim()) return [{ text, highlight: false }];
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map(p => ({ text: p, highlight: p.toLowerCase() === query.toLowerCase() }));
}



// Ein Entwurf = angelegt, aber ohne generierten Inhalt (zählt nicht als echte Bewerbung)
const istEntwurf = (s) =>
  !s.cv_data && !s.cv_rohtext && !s.generiert_am &&
  !s.analyse && !s.analyse_struktur && !s._runningJobId;

export { THEMES, T, MONTH_NAMES, DAY_NAMES, PROFIL_FELDER, berechneBasisPct, naechsterProfilSchritt, parseMatchScore, extractPreviewTitle, PLATZHALTER, useLongPress, CAL_MONTHS, CAL_DOW, buildCalGrid, calTodayStr, formatMobileDate, formatTagLabel, highlightParts, istEntwurf };
